import { defineStore } from 'pinia';
import { ClientFeatures, ClientRoleEnum, ExtendedBusinessSiteData, IBranch, IBranchInput, IService, MAIN_BRANCH_ID, Plans } from '@torim-app/types';
import type { IAdminStore } from '@app/types';
import { AdminRouteNames } from '../router/route-names';
import { SharedApi } from '@app/api/admin/shared-api';
import { checkFeatureIsAvailable, withMainBranch } from '@torim-app/utils';
import { ServicesApi, SettingsApi } from '@app/api/admin';

export const useAdminStore = defineStore('admin', {
  state: (): IAdminStore => ({
    businessSiteData: {} as ExtendedBusinessSiteData,
    currentPage: AdminRouteNames.Home,
    userId: '',
    isMainClient: false,
    clientRole: ClientRoleEnum.Employee,
    fetchingInProgress: false,
    providers: [],
    notifications: [],
    services: [],
    complementaryServices: [],
    privateServices: []
  }),
  getters: {
    getUserId: (state): string => {
      return state.userId;
    },
    getMainClient: (state): boolean => {
      return state.isMainClient;
    },
    getClientRole: (state): ClientRoleEnum => {
      return state.clientRole;
    },
    getIsAdmin: (state): boolean => {
      return state.clientRole === ClientRoleEnum.Admin;
    },
    getIsOperator: (state): boolean => {
      return state.clientRole === ClientRoleEnum.Operator;
    },
    getIsReadOnly: (state): boolean => {
      return state.clientRole === ClientRoleEnum.ReadOnly;
    },
    getCanEdit: (state): boolean => {
      return state.clientRole !== ClientRoleEnum.ReadOnly;
    },
    getCanManageOthers: (state): boolean => {
      return state.clientRole === ClientRoleEnum.Admin || state.clientRole === ClientRoleEnum.Operator;
    },
    // Resolved per request by the server (not from the session token), so it may be
    // undefined until businessSiteData lands — treat that as allowed.
    getCanViewCustomerContact: (state): boolean => {
      return state.businessSiteData?.canViewCustomerContact !== false;
    },
    // Resolved per request by the server: true for managers, and for employees the owner
    // granted the branch calendar to. Undefined until businessSiteData lands => no access.
    getCanViewBranchCalendar: (state): boolean => {
      return state.businessSiteData?.canViewBranchCalendar === true;
    },
    getBusinessSiteData(state): ExtendedBusinessSiteData {
      return state.businessSiteData;
    },
    // Active branches only — deactivated (soft-deleted) branches are hidden
    // everywhere in the admin UI, mirroring the booking site.
    getBranches(state): IBranch[] {
      return (state.businessSiteData.branches ?? []).filter(b => b.active !== false);
    },
    // Branch UI appears once there is at least one additional branch beyond the
    // implicit main branch (i.e. two or more physical locations).
    hasBranches(): boolean {
      return this.getBranches.length >= 1;
    },
    // Selectable/taggable branches: the main branch (the business itself) first,
    // then any additional active branches.
    getBranchSelectOptions(state): IBranch[] {
      return withMainBranch(state.businessSiteData, this.getBranches, '(ראשי)');
    },
    getBusinessTitle(state): string {
      return state.businessSiteData.title;
    },
    getCurrentPage(state): string {
      return state.currentPage;
    },
    getServices(state): IService[] {
      return [...(state.services ?? []), ...(state.privateServices ?? []), ...(state.complementaryServices ?? [])].filter(x => x.visible);
    },
    getRegularServices(state): IService[] {
      return [...(state.services || [])].filter(x => x.visible);
    },
    getPrivateServices(state): IService[] {
      return [...(state.privateServices || [])].filter(x => x.visible);
    },
    getComplementaryServices(state): IService[] {
      return [...(state.complementaryServices || [])].filter(x => x.visible);
    },
    getFetchingInProgress(state): boolean {
      return state.fetchingInProgress;
    },
    getProviders(state) {
      return state.providers;
    },
    getSavedStates(state) {
      return state.businessSiteData.savedStates;
    },
    getIsFeatureAvailable(state) {
      return (feature: ClientFeatures) => {
        if (state.businessSiteData.plan === Plans.PREMIUM) return true;
        return checkFeatureIsAvailable(feature, state.businessSiteData.plan);
      };
    }
  },
  actions: {
    async fetchBusinessData() {
      try {
        this.fetchingInProgress = true;
        const [siteData, providers, groupedServices] = await Promise.all([
          SharedApi.fetchBusinessData(),
          SharedApi.fetchProviders(),
          ServicesApi.getGroupedServices()
        ]);

        Object.assign(this.businessSiteData, siteData);
        this.providers.splice(0);
        const currentUser = providers.find(p => p.userId === this.userId);
        const others = providers.filter(p => p.userId !== this.userId);
        if (!currentUser || this.clientRole === ClientRoleEnum.Operator) {
          this.providers.push(...others);
        } else {
          this.providers.push(currentUser, ...others);
        }

        this.services.splice(0);
        this.services.push(...groupedServices.regular);

        this.privateServices.splice(0);
        this.privateServices.push(...groupedServices.hidden);

        this.complementaryServices.splice(0);
        this.complementaryServices.push(...groupedServices.complementary);

        this.getNotifications();
      } catch (e) {
        console.error(e);
        throw e;
      } finally {
        this.fetchingInProgress = false;
      }
    },
    getNotifications() {
      SharedApi.getAllNotifications()
        .then(res => {
          this.notifications.splice(0);
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          this.notifications.push(...res);
        });
    },
    updateSession(userId: string) {
      this.userId = userId;
    },
    setIsMainClient(isMainClient: boolean) {
      this.isMainClient = isMainClient;
    },
    setClientRole(role: ClientRoleEnum) {
      this.clientRole = role;
      this.isMainClient = role === ClientRoleEnum.Admin;
    },
    setPage(page: AdminRouteNames) {
      this.currentPage = page;
    },
    async fetchBranches() {
      const branches = await SettingsApi.getBranches();
      this.businessSiteData.branches = branches;
      return branches;
    },
    async createBranch(input: IBranchInput) {
      const branch = await SettingsApi.createBranch(input);
      this.businessSiteData.branches = [...(this.businessSiteData.branches ?? []), branch];
      return branch;
    },
    async updateBranch(branchId: string, input: IBranchInput) {
      const updated = await SettingsApi.updateBranch(branchId, input);
      if (branchId === MAIN_BRANCH_ID) {
        // The main branch mirrors the business's own primary-location fields — except
        // its name, which is stored separately so renaming the branch never renames
        // the business.
        if (input.name?.trim()) this.businessSiteData.mainBranchName = input.name.trim();
        if (input.address !== undefined) this.businessSiteData.location = input.address;
        if (input.phone !== undefined) this.businessSiteData.phone = input.phone;
        this.businessSiteData.locationImage = updated.locationImage;
      } else {
        this.businessSiteData.branches = (this.businessSiteData.branches ?? []).map(b => b.id === branchId ? updated : b);
      }
      return updated;
    },
    async deleteBranch(branchId: string) {
      await SettingsApi.deleteBranch(branchId);
      this.businessSiteData.branches = (this.businessSiteData.branches ?? []).filter(b => b.id !== branchId);
    },
    setFetchingInProgress() {
      this.fetchingInProgress = true;
    },
    setFetchingEnded() {
      this.fetchingInProgress = false;
    }
  }
});
